<template>
  <div>
    <LabelReset
      v-if="label || slots.label"
      :initialValue="initialValue"
      v-model="modelValue"
      :forInput="inputId || name"
      id="chip-input-label-reset"
    >
      <slot name="label">{{ label }}</slot>
    </LabelReset>
    <div
      class="p-inputtext cursor-text w-full focus-within:border-[var(--p-primary-color)]"
      :class="{ 'p-invalid': Boolean(errorMessage) }"
      @click="focusInput"
      id="chip-input-wrapper"
    >
      <div class="flex flex-wrap gap-2">
        <Chip
          v-for="item in internalItems"
          :key="item.id"
          :label="item.value"
          removable
          @remove="removeItem(item.id)"
          class="chip-item"
        />
        <InputText class="hidden" />
        <input
          ref="inputRef"
          v-model="inputValue"
          type="text"
          :id="inputId || name"
          :name="name"
          class="outline-none flex-grow chip-text-input"
          :placeholder="internalItems.length === 0 ? placeholder : ''"
          @keydown="handleKeyDown"
          @paste="handlePaste"
          @input="handleInputChange"
        />
      </div>
    </div>
    <Message v-if="errorMessage" id="error-message" severity="error" size="small" variant="simple">
      {{ errorMessage }}
    </Message>
    <Message
      v-if="helperText || slots.helperText"
      id="helper-text-message"
      severity="secondary"
      size="small"
      variant="simple"
    >
      <slot name="helperText">{{ helperText }}</slot>
    </Message>
  </div>
</template>

<script setup lang="ts">
import { isEqual, uniqueId } from 'lodash-es'
import Chip from 'primevue/chip'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import { computed, defineModel, ref, useSlots, watch } from 'vue'

import LabelReset from '@/common/components/molecules/LabelReset.vue'

interface ValidationResult {
  isValid: boolean
  message?: string
}

// We use an internal array of objects (`internalItems`) with unique IDs
// to ensure stable keys for the v-for loop rendering the Chips.
// This prevents potential issues with Vue's reconciliation when items are added/
// removed, especially if values could be duplicated or change.
// Watchers sync this internal state with the external `modelValue` (string[]).
interface ChipItem {
  id: string
  value: string
}

interface Props {
  helperText?: string
  initialValue?: string[]
  name?: string
  label?: string
  inputId?: string
  placeholder?: string
  // eslint-disable-next-line no-unused-vars
  validationFn?: (item: string) => ValidationResult
  allowDuplicates?: boolean
  duplicateMessage?: string
  invalidMessage?: string
  errors?: string[]
}
const props = withDefaults(defineProps<Props>(), {
  helperText: undefined,
  initialValue: undefined,
  name: undefined,
  label: undefined,
  inputId: undefined,
  placeholder: 'Enter values',
  validationFn: (_item: string) => ({ isValid: !!_item?.trim(), message: 'Invalid value' }),
  allowDuplicates: false,
  duplicateMessage: 'This item is already added.',
  invalidMessage: 'Please enter a valid value.',
  errors: undefined,
})

const modelValue = defineModel<string[]>({ default: () => [] })
const slots = useSlots()

const inputValue = ref<string>('')
const isInvalid = ref<boolean>(false)
const isDuplicate = ref<boolean>(false)
const inputRef = ref<HTMLInputElement | null>(null)

// Internal state using objects with unique IDs
const internalItems = ref<ChipItem[]>([])
// Sync modelValue (external string[]) to internalItems (object[])
watch(
  modelValue,
  newModelValue => {
    // Avoid infinite loop: Check if the values AND order are already in sync
    const currentValues = internalItems.value.map(item => item.value)
    if (!isEqual(newModelValue, currentValues)) {
      internalItems.value = newModelValue.map(value => ({
        id: uniqueId('chip_'),
        value,
      }))
    }
  },
  { immediate: true, deep: true },
)
// Sync internalItems (object[]) back to modelValue (external string[])
watch(
  internalItems,
  newInternalItems => {
    const newValues = newInternalItems.map(item => item.value)
    // Avoid infinite loop: Check if modelValue already matches in value AND order
    if (!isEqual(modelValue.value, newValues)) {
      modelValue.value = newValues
    }
  },
  { deep: true },
)

const hasExternalErrors = computed<boolean>(() => Boolean(props.errors && props.errors.length > 0))
const errorMessage = computed<string | undefined>(() => {
  if (hasExternalErrors.value) {
    return props.errors![0]
  } else if (isInvalid.value) {
    return props.invalidMessage
  } else if (isDuplicate.value) {
    return props.duplicateMessage
  }
  return undefined
})

function addItem(itemValue: string): void {
  const trimmedItem = itemValue.trim()
  if (!trimmedItem) return

  const validationResult = props.validationFn(trimmedItem)
  const currentValues = internalItems.value.map(i => i.value)

  if (validationResult.isValid) {
    if (props.allowDuplicates || !currentValues.includes(trimmedItem)) {
      // Add to internal state
      internalItems.value.push({
        id: uniqueId('chip_'),
        value: trimmedItem,
      })
      inputValue.value = ''
      isInvalid.value = false
      isDuplicate.value = false
    } else {
      isDuplicate.value = true
      isInvalid.value = false
    }
  } else {
    isInvalid.value = true
    isDuplicate.value = false
  }
}

// Now accepts ID instead of index
function removeItem(id: string): void {
  const index = internalItems.value.findIndex(item => item.id === id)
  if (index !== -1) {
    internalItems.value.splice(index, 1)
  }
  isDuplicate.value = false
  isInvalid.value = false
}

function handleKeyDown(e: Event): void {
  const event = e as KeyboardEvent
  // Handle Enter and Comma keys
  if (event.key === 'Enter' || event.key === ',') {
    e.preventDefault()
    addItem(inputValue.value)
  }
  // Handle Tab key only if there is input value
  else if (event.key === 'Tab' && inputValue.value.trim()) {
    e.preventDefault()
    addItem(inputValue.value)
  }
  // Handle Backspace key - remove the last item by ID
  else if (event.key === 'Backspace' && !inputValue.value && internalItems.value.length > 0) {
    const lastItem = internalItems.value[internalItems.value.length - 1]
    removeItem(lastItem.id)
  }
  // Reset flags on other key presses if there's input
  else {
    if (inputValue.value) {
      isInvalid.value = false
      isDuplicate.value = false
    }
  }
}

function handlePaste(e: ClipboardEvent): void {
  e.preventDefault()
  const pastedText = e.clipboardData?.getData('text') || ''
  const pastedItems = pastedText.split(/[,;\s\n]+/)

  let encounteredInvalid = false
  let encounteredDuplicate = false
  const currentValues = internalItems.value.map(i => i.value)

  pastedItems.forEach(item => {
    const trimmed = item.trim()
    if (trimmed) {
      const validationResult = props.validationFn(trimmed)
      if (validationResult.isValid) {
        if (props.allowDuplicates || !currentValues.includes(trimmed)) {
          // Use addItem to handle adding to internalItems
          addItem(trimmed)
          // We don't need validItemsAdded anymore as addItem updates internalItems
        } else {
          encounteredDuplicate = true
        }
      } else {
        encounteredInvalid = true
      }
    }
  })

  isInvalid.value = encounteredInvalid
  isDuplicate.value = encounteredDuplicate && !encounteredInvalid

  // Clear input if any valid items were processed by addItem
  if (pastedItems.some(p => p.trim() && props.validationFn(p.trim()).isValid)) {
    inputValue.value = '' // Clear input as items were likely added
  }
  // If only invalid text was pasted, keep it in the input for correction
  else if (pastedText.trim() && encounteredInvalid && !encounteredDuplicate) {
    inputValue.value = pastedText
  }
  // If only duplicates were pasted, clear input
  else if (pastedText.trim() && encounteredDuplicate && !encounteredInvalid) {
    inputValue.value = ''
  }
}

function handleInputChange(e: Event): void {
  const value = (e.target as HTMLInputElement).value

  // If the input value ends with a comma, treat it as an add trigger
  if (value.endsWith(',')) {
    const itemPart = value.slice(0, -1)
    addItem(itemPart)
  } else {
    inputValue.value = value
    if (value) {
      isInvalid.value = false
      isDuplicate.value = false
    }
  }
}

function focusInput(): void {
  inputRef.value?.focus()
}
</script>
